import React from "react";
import { useCurrentFrame } from "../use-current-frame";
import { interpolate } from "../interpolate";
import { Easing } from "../Easing";
import styled from "styled-components";
import { useVideoConfig } from "../use-video-config";
import { useProject } from "../../store/projectContext";
import { Sequence } from "../Sequence";

const SlideWrapper = styled.div``;

/**
 * SlideTransitions — wraps a scene with entry/exit transition effects.
 *
 * Props:
 *   - slide: scene index
 *   - cumulatedDuration: cumulative duration in seconds up to end of this scene
 *   - children: the scene content to render
 */
const SlideTransitions = ({ slide, cumulatedDuration, children }) => {
  const frame = useCurrentFrame();
  const videoConfig = useVideoConfig();
  const project = useProject();
  const scenes = project?.scenes || [];
  const scene = scenes[slide];
  const workspaceWidth = project?.width || 800;
  const workspaceHeight = project?.height || 450;

  if (!scene) return null;

  const currentSlideDuration = scene.duration || 4;

  // Get transition config from scene data (new structure uses scene.transition.effect)
  let sceneTransition = scene.transition && scene.transition.effect && scene.transition.effect !== 'none'
    ? { type: scene.transition.effect, duration: scene.transition.duration || 0.5, direction: scene.transition.direction, wipeDirection: scene.transition.wipeDirection, clockWise: scene.transition.clockWise }
    : null;
  const nextScene = scenes[slide + 1];
  let nextSceneTransition = nextScene?.transition && nextScene.transition.effect && nextScene.transition.effect !== 'none'
    ? { type: nextScene.transition.effect, duration: nextScene.transition.duration || 0.5, direction: nextScene.transition.direction, wipeDirection: nextScene.transition.wipeDirection, clockWise: nextScene.transition.clockWise }
    : null;

  const direction = sceneTransition?.direction || "left";
  const wipeDirection = sceneTransition?.wipeDirection || "in";
  const isAntiClock = sceneTransition?.clockWise || false;

  const fromFrame = Math.round((cumulatedDuration - currentSlideDuration) * videoConfig.fps) + 1;
  const toFrame = Math.round(cumulatedDuration * videoConfig.fps);

  let toLessFirst = 0;
  let toLessLast = 0;
  let style = {};
  let innerStyle = {
    width: workspaceWidth + "px",
    height: workspaceHeight + "px",
  };

  const currentTransitionDuration =
    sceneTransition && typeof sceneTransition.duration === "number"
      ? Math.min(sceneTransition.duration, 0.5) / 2
      : 0.125;

  const nextTransitionDuration =
    nextSceneTransition && typeof nextSceneTransition.duration === "number"
      ? nextSceneTransition.duration / 2
      : 0.125;

  // Skip entry transition for the very first scene — matches preview behavior.
  const thisTransition = (slide === 0) ? "" : (sceneTransition?.type || "");
  const nextTransition = nextSceneTransition?.type || "";

  let transitionComponents = null;

  // === EXIT transition (next scene pushing this one out) ===
  if (nextTransition) {
    if (nextTransition === "slide") {
      let valueArray = [0, -1 * workspaceWidth];
      const nextDir = nextSceneTransition?.direction || "left";
      if (nextDir === "right") valueArray = [0, workspaceWidth];
      else if (nextDir === "top") valueArray = [0, -1 * workspaceHeight];
      else if (nextDir === "bottom") valueArray = [0, workspaceHeight];

      // Exit slide-out plays entirely within the next scene's window, starting at the cut.
      const frameArray = [
        toFrame + 1,
        toFrame + 1 + nextTransitionDuration * 2 * videoConfig.fps,
      ];

      const slideVal = interpolate(frame, frameArray, valueArray, {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });

      if (nextDir === "top" || nextDir === "bottom") {
        style.transform = `translate(0, ${slideVal}px)`;
      } else {
        style.transform = `translate(${slideVal}px, 0)`;
      }
    }
    toLessLast = nextTransitionDuration * 2 * videoConfig.fps;
  }

  // === ENTRY transition (this scene entering) ===
  if (thisTransition) {
    toLessFirst = 0; // transition plays within this scene's own time window
    // Starts exactly at the scene boundary and plays forward the full duration.
    const startFrame = fromFrame;
    const endFrame = fromFrame + currentTransitionDuration * 2 * videoConfig.fps;

    if (thisTransition === "slide") {
      let valueArray = [workspaceWidth, 0];
      if (direction === "right") valueArray = [-1 * workspaceWidth, 0];
      else if (direction === "top") valueArray = [workspaceHeight, 0];
      else if (direction === "bottom") valueArray = [-1 * workspaceHeight, 0];

      const slideVal = interpolate(frame, [startFrame, endFrame], valueArray, {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });

      if (direction === "top" || direction === "bottom") {
        style.transform = `translate(0, ${slideVal}px)`;
      } else {
        style.transform = `translate(${slideVal}px, 0)`;
      }
    } else if (thisTransition === "slide-left") {
      let valueArray = [workspaceWidth, 0];
      const slideVal = interpolate(frame, [startFrame, endFrame], valueArray, {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });
      style.transform = `translate(${slideVal}px, 0)`;
    } else if (thisTransition === "slide-right") {
      let valueArray = [-1 * workspaceWidth, 0];
      const slideVal = interpolate(frame, [startFrame, endFrame], valueArray, {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });
      style.transform = `translate(${slideVal}px, 0)`;
    } else if (thisTransition === "slide-top") {
      let valueArray = [workspaceHeight, 0];
      const slideVal = interpolate(frame, [startFrame, endFrame], valueArray, {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });
      style.transform = `translate(0, ${slideVal}px)`;
    } else if (thisTransition === "slide-bottom") {
      let valueArray = [-1 * workspaceHeight, 0];
      const slideVal = interpolate(frame, [startFrame, endFrame], valueArray, {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });
      style.transform = `translate(0, ${slideVal}px)`;
    } else if (thisTransition === "opacity") {
      const opacity = interpolate(frame, [startFrame, endFrame], [0, 1], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
        easing: Easing.cubic,
      });
      style.opacity = opacity;
    } else if (thisTransition === "lineWipe") {
      const mask = interpolate(frame, [startFrame, endFrame], [0, 100], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });
      const angle = direction === "right" ? 90 : direction === "bottom" ? 180 : direction === "top" ? 0 : 270;
      style.WebkitMask = `linear-gradient(${angle}deg, rgb(0,0,0) ${mask}%, rgba(0,0,0,0) ${mask}%)`;
    } else if (thisTransition === "circleWipe") {
      let valueArray = [100, 0];
      if (wipeDirection === "out") valueArray = [0, 100];

      const mask = interpolate(frame, [startFrame, endFrame], valueArray, {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });

      if (wipeDirection === "out") {
        style.WebkitMask = `radial-gradient(circle at center, rgb(0,0,0) ${mask}%, rgba(0,0,0,0) ${mask}%)`;
      } else {
        style.WebkitMask = `radial-gradient(circle at center, rgba(0,0,0,0) ${mask}%, rgb(0,0,0) ${mask}%)`;
      }
    } else if (thisTransition === "zoom") {
      const valueArray = wipeDirection === "out" ? [10, 1] : [0.1, 1];
      const scale = interpolate(frame, [startFrame, endFrame], valueArray, {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });
      style.transform = `scale(${scale})`;
    } else if (thisTransition === "rotate") {
      let origin = "top left";
      if (direction === "top-right") origin = "top right";
      else if (direction === "bottom-right") origin = "bottom right";
      else if (direction === "bottom-left") origin = "bottom left";

      const valueArray = [isAntiClock ? 90 : -90, 0];
      const rotate = interpolate(frame, [startFrame, endFrame], valueArray, {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });

      style.transform = `rotate(${rotate}deg)`;
      style.transformOrigin = origin;
    } else if (thisTransition === "drop") {
      const diff = endFrame - startFrame;
      const toMultiply = direction === "top" || direction === "left" ? 1 : -1;
      const toCalculate = direction === "left" || direction === "right" ? workspaceWidth : workspaceHeight;

      const translateVal = interpolate(
        frame,
        [startFrame, startFrame + diff * 0.25, startFrame + diff * 0.5, startFrame + diff * 0.65, startFrame + diff * 0.75, startFrame + diff * 0.80, startFrame + diff * 0.90, startFrame + diff * 0.925, startFrame + diff * 0.95, startFrame + diff * 0.975, endFrame],
        [toCalculate * toMultiply * 1, toCalculate * toMultiply * 0.75, toCalculate * toMultiply * 0.50, toCalculate * toMultiply * 0.25, 0, toCalculate * toMultiply * 0.20, 0, toCalculate * toMultiply * 0.075, 0, toCalculate * toMultiply * 0.025, 0],
        {
          easing: Easing.bezier(0.165, 0.44, 0.64, 1),
        }
      );

      if (direction === "top" || direction === "bottom") {
        style.transform = `translateY(${translateVal}px)`;
      } else {
        style.transform = `translateX(${translateVal}px)`;
      }
    } else if (thisTransition === "stack") {
      let valueArray = [workspaceWidth, 0];
      if (direction === "right") valueArray = [-1 * workspaceWidth, 0];
      else if (direction === "top") valueArray = [workspaceHeight, 0];
      else if (direction === "bottom") valueArray = [-1 * workspaceHeight, 0];

      const slideVal = interpolate(frame, [startFrame, endFrame], valueArray, {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });

      if (direction === "top" || direction === "bottom") {
        style.transform = `translate(0, ${slideVal}px)`;
      } else {
        style.transform = `translate(${slideVal}px, 0)`;
      }
    } else if (thisTransition === "split") {
      let valueArray = [50, 0];
      if (wipeDirection === "in") valueArray = [0, 50];

      const val = interpolate(frame, [startFrame, endFrame], valueArray, {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });

      let clipPath = `inset(${val}% 0%)`;
      if (direction === "top" && wipeDirection === "in") {
        clipPath = `polygon(0 ${val}%, 0% 100%, 0 100%, 0 0, 100% 0, 100% 100%, 0 100%, 0 ${100 - val}%, 100% ${100 - val}%, 100% ${val}%)`;
      } else if (direction === "left" && wipeDirection === "out") {
        clipPath = `inset(0% ${val}%)`;
      } else if (direction === "left" && wipeDirection === "in") {
        clipPath = `polygon(0 100%, 100% 100%, 100% 100%, 100% 0, ${100 - val}% 0%, ${100 - val}% 100%, ${val}% 100%, ${val}% 0, 0 0)`;
      }
      style.clipPath = clipPath;
    } else if (thisTransition === "shutter") {
      const val = interpolate(frame, [startFrame, endFrame], [0, 20], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });
      
      if (direction === "left") {
        style.clipPath = `polygon(100% 100%, 100% 0, ${100 - val}% 0, ${100 - val}% 100%, 80% 100%, 80% 0, ${80 - val}% 0%, ${80 - val}% 100%, 60% 100%, 60% 0%, ${60 - val}% 0%, ${60 - val}% 100%, 40% 100%, 40% 0%, ${40 - val}% 0%, ${40 - val}% 100%, 20% 100%, 20% 0%, ${20 - val}% 0%, ${20 - val}% 100%)`;
      } else {
        style.clipPath = `polygon(0 ${20 - val}%, 100% ${20 - val}%, 100% 20%, 0 20%, 0 ${40 - val}%, 100% ${40 - val}%, 100% 40%, 0 40%, 0 ${60 - val}%, 100% ${60 - val}%, 100% 60%, 0 60%, 0 ${80 - val}%, 100% ${80 - val}%, 100% 80%, 0 80%, 0 ${100 - val}%, 100% ${100 - val}%, 100% 100%, 0 100%)`;
      }
    } else if (thisTransition === "stripe") {
      const val = interpolate(frame, [startFrame, endFrame], [0, 550], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });

      if (direction === "right") {
        style.clipPath = `polygon(0 0, 0 ${val < 100 ? val : 100}%, 10% ${val < 100 ? val : 100}%, 10% 0, 10% ${val < 50 ? 0 : val < 150 ? val - 50 : 100}%, 20% ${val < 50 ? 0 : val < 150 ? val - 50 : 100}%, 20% 0, 20% ${val < 100 ? 0 : val < 200 ? val - 100 : 100}%, 30% ${val < 100 ? 0 : val < 200 ? val - 100 : 100}%, 30% 0, 30% ${val < 150 ? 0 : val < 250 ? val - 150 : 100}%, 40% ${val < 150 ? 0 : val < 250 ? val - 150 : 100}%, 40% 0, 40% ${val < 200 ? 0 : val < 300 ? val - 200 : 100}%, 50% ${val < 200 ? 0 : val < 300 ? val - 200 : 100}%, 50% 0, 50% ${val < 250 ? 0 : val < 350 ? val - 250 : 100}%, 60% ${val < 250 ? 0 : val < 350 ? val - 250 : 100}%, 60% 0, 60% ${val < 300 ? 0 : val < 400 ? val - 300 : 100}%, 70% ${val < 300 ? 0 : val < 400 ? val - 300 : 100}%, 70% 0, 70% ${val < 350 ? 0 : val < 450 ? val - 350 : 100}%, 80% ${val < 350 ? 0 : val < 450 ? val - 350 : 100}%, 80% 0, 80% ${val < 400 ? 0 : val < 500 ? val - 400 : 100}%, 90% ${val < 400 ? 0 : val < 500 ? val - 400 : 100}%, 90% 0, 90% ${val < 450 ? 0 : val < 550 ? val - 450 : 100}%, 100% ${val < 450 ? 0 : val < 550 ? val - 450 : 100}%, 100% 0)`;
      } else if (direction === "left") {
        style.clipPath = `polygon(0 100%, 0% ${val < 100 ? (100 - val) : 0}%, 10% ${val < 100 ? (100 - val) : 0}%, 10% 100%, 10% ${val < 50 ? 100 : val < 150 ? (150 - val) : 0}%, 20% ${val < 50 ? 100 : val < 150 ? (150 - val) : 0}%, 20% 100%, 20% ${val < 100 ? 100 : val < 200 ? (200 - val) : 0}%, 30% ${val < 100 ? 100 : val < 200 ? (200 - val) : 0}%, 30% 100%, 30% ${val < 150 ? 100 : val < 250 ? (250 - val) : 0}%, 40% ${val < 150 ? 100 : val < 250 ? (250 - val) : 0}%, 40% 100%, 40% ${val < 200 ? 100 : val < 300 ? (300 - val) : 0}%, 50% ${val < 200 ? 100 : val < 300 ? (300 - val) : 0}%, 50% 100%, 50% ${val < 250 ? 100 : val < 350 ? (350 - val) : 0}%, 60% ${val < 250 ? 100 : val < 350 ? (350 - val) : 0}%, 60% 100%, 60% ${val < 300 ? 100 : val < 400 ? (400 - val) : 0}%, 70% ${val < 300 ? 100 : val < 400 ? (400 - val) : 0}%, 70% 100%, 70% ${val < 350 ? 100 : val < 450 ? (450 - val) : 0}%, 80% ${val < 350 ? 100 : val < 450 ? (450 - val) : 0}%, 80% 100%, 80% ${val < 400 ? 100 : val < 500 ? (500 - val) : 0}%, 90% ${val < 400 ? 100 : val < 500 ? (500 - val) : 0}%, 90% 100%, 90% ${val < 450 ? 100 : val < 550 ? (550 - val) : 0}%, 100% ${val < 450 ? 100 : val < 550 ? (550 - val) : 0}%, 100% 100%)`;
      } else if (direction === "top") {
        style.clipPath = `polygon(100% 0, ${val < 100 ? (100 - val) : 0}% 0%,  ${val < 100 ? (100 - val) : 0}% 10%, 100% 10%, ${val < 50 ? 100 : val < 150 ? (150 - val) : 0}% 10%, ${val < 50 ? 100 : val < 150 ? (150 - val) : 0}% 20%, 100% 20%, ${val < 100 ? 100 : val < 200 ? (200 - val) : 0}% 20%, ${val < 100 ? 100 : val < 200 ? (200 - val) : 0}% 30%, 100% 30%, ${val < 150 ? 100 : val < 250 ? (250 - val) : 0}% 30%, ${val < 150 ? 100 : val < 250 ? (250 - val) : 0}% 40%, 100% 40%, ${val < 200 ? 100 : val < 300 ? (300 - val) : 0}% 40%, ${val < 200 ? 100 : val < 300 ? (300 - val) : 0}% 50%, 100% 50%, ${val < 250 ? 100 : val < 350 ? (350 - val) : 0}% 50%, ${val < 250 ? 100 : val < 350 ? (350 - val) : 0}% 60%, 100% 60%, ${val < 300 ? 100 : val < 400 ? (400 - val) : 0}% 60%, ${val < 300 ? 100 : val < 400 ? (400 - val) : 0}% 70%, 100% 70%, ${val < 350 ? 100 : val < 450 ? (450 - val) : 0}% 70%, ${val < 350 ? 100 : val < 450 ? (450 - val) : 0}% 80%, 100% 80%, ${val < 400 ? 100 : val < 500 ? (500 - val) : 0}% 80%, ${val < 400 ? 100 : val < 500 ? (500 - val) : 0}% 90%, 100% 90%, ${val < 450 ? 100 : val < 550 ? (550 - val) : 0}% 90%, ${val < 450 ? 100 : val < 550 ? (550 - val) : 0}% 100%, 100% 100%)`;
      } else {
        style.clipPath = `polygon(0 0, ${val < 100 ? val : 100}% 0, ${val < 100 ? val : 100}% 10%, 0 10%, ${val < 50 ? 0 : val < 150 ? val - 50 : 100}% 10%, ${val < 50 ? 0 : val < 150 ? val - 50 : 100}% 20%, 0 20%, ${val < 100 ? 0 : val < 200 ? val - 100 : 100}% 20%, ${val < 100 ? 0 : val < 200 ? val - 100 : 100}% 30%, 0 30%, ${val < 150 ? 0 : val < 250 ? val - 150 : 100}% 30%, ${val < 150 ? 0 : val < 250 ? val - 150 : 100}% 40%, 0 40%, ${val < 200 ? 0 : val < 300 ? val - 200 : 100}% 40%, ${val < 200 ? 0 : val < 300 ? val - 200 : 100}% 50%, 0 50%, ${val < 250 ? 0 : val < 350 ? val - 250 : 100}% 50%, ${val < 250 ? 0 : val < 350 ? val - 250 : 100}% 60%, 0 60%, ${val < 300 ? 0 : val < 400 ? val - 300 : 100}% 60%, ${val < 300 ? 0 : val < 400 ? val - 300 : 100}% 70%, 0 70%, ${val < 350 ? 0 : val < 450 ? val - 350 : 100}% 70%, ${val < 350 ? 0 : val < 450 ? val - 350 : 100}% 80%, 0 80%, ${val < 400 ? 0 : val < 500 ? val - 400 : 100}% 80%, ${val < 400 ? 0 : val < 500 ? val - 400 : 100}% 90%, 0 90%, ${val < 450 ? 0 : val < 550 ? val - 450 : 100}% 90%, ${val < 450 ? 0 : val < 550 ? val - 450 : 100}% 100%, 0 100%)`;
      }
    } else if (thisTransition === "fan-up") {
      const rotate = interpolate(frame, [startFrame, endFrame], [isAntiClock ? 90 : -90, 0], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });
      const scale = interpolate(frame, [startFrame, endFrame], [3, 1], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });
      const origin = direction.replace("-", " ");

      transitionComponents = (
        <>
          <div style={{
            background: "brown",
            transformOrigin: origin,
            position: "absolute",
            transform: `rotate(${rotate - 40}deg) scale(${scale})`,
            ...innerStyle
          }}></div>
          <div style={{
            background: "yellow",
            transformOrigin: origin,
            position: "absolute",
            transform: `rotate(${rotate - 30}deg) scale(${scale})`,
            ...innerStyle
          }}></div>
          <div style={{
            background: "blue",
            transformOrigin: origin,
            position: "absolute",
            transform: `rotate(${rotate - 20}deg) scale(${scale})`,
            ...innerStyle
          }}></div>
          <div style={{
            background: "red",
            transformOrigin: origin,
            position: "absolute",
            transform: `rotate(${rotate - 10}deg) scale(${scale})`,
            ...innerStyle
          }}></div>
        </>
      );
      style.transform = `rotate(${rotate}deg) scale(${scale})`;
      style.transformOrigin = origin;
    } else if (thisTransition === "fan-rotate") {
      const rotate = interpolate(frame, [startFrame, endFrame], [isAntiClock ? 90 : -90, 0], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });
      const scale = interpolate(frame, [startFrame, endFrame], [3, 1], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });
      const origin = direction.replace("-", " ");

      transitionComponents = (
        <>
          <div style={{
            background: "brown",
            transformOrigin: origin,
            position: "absolute",
            transform: `rotate(${rotate + (40 * (!isAntiClock ? 1 : -1))}deg) scale(${scale})`,
            ...innerStyle
          }}></div>
          <div style={{
            background: "yellow",
            transformOrigin: origin,
            position: "absolute",
            transform: `rotate(${rotate + (30 * (!isAntiClock ? 1 : -1))}deg) scale(${scale})`,
            ...innerStyle
          }}></div>
          <div style={{
            background: "blue",
            transformOrigin: origin,
            position: "absolute",
            transform: `rotate(${rotate + (20 * (!isAntiClock ? 1 : -1))}deg) scale(${scale})`,
            ...innerStyle
          }}></div>
          <div style={{
            background: "red",
            transformOrigin: origin,
            position: "absolute",
            transform: `rotate(${rotate + (10 * (!isAntiClock ? 1 : -1))}deg) scale(${scale})`,
            ...innerStyle
          }}></div>
        </>
      );
      style.transform = `rotate(${rotate}deg) scale(${scale})`;
      style.transformOrigin = origin;
    } else if (thisTransition === "scale-up") {
      const valueArray = [0.1, 100];
      const origin = direction.replace("-", " ");
      const val = interpolate(frame, [startFrame, endFrame], valueArray, {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });
      const scale = val / 100;
      style.transformOrigin = origin;
      
      if (direction === "bottom-right") {
        style.transform = `scale(${scale}) translate(0px, 0px)`;
        style.transformOrigin = "top left";
      } else if (direction === "bottom-left") {
        style.transform = `scale(${scale}) translate(${(workspaceWidth * (1 - scale)) * (100 / val)}px, 0px)`;
        style.transformOrigin = "top right";
      } else if (direction === "top-right") {
        style.transform = `scale(${scale}) translate(0px, ${(workspaceHeight * (1 - scale)) * (100 / val)}px)`;
        style.transformOrigin = "bottom left";
      } else {
        style.transform = `scale(${scale}) translate(${(workspaceWidth * (1 - scale)) * (100 / val)}px, ${(workspaceHeight * (1 - scale)) * (100 / val)}px)`;
      }
    } else if (thisTransition === "cube-rotate") {
      const diff = endFrame - startFrame;
      if (frame < (startFrame + (diff / 2))) {
        const rotate = interpolate(frame, [startFrame, endFrame], [-135, 0], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });
        style.transform = `rotateY(${rotate}deg)`;
      } else {
        const rotate = interpolate(frame, [startFrame, endFrame], [0.1, 1], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        });
        style.transform = `rotateY(${rotate}deg)`;
      }
    } else if (thisTransition === "scaleR") {
      const scale = interpolate(frame, [startFrame, endFrame], [1, 1.25], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      });
      style.transform = `scale(${scale})`;
    }
  }

  // If no transitions at all on this scene, render as simple Sequence
  if (!thisTransition && !nextTransition) {
    return (
      <Sequence
        key={slide}
        from={Math.max(0, fromFrame - 1)}
        premountFor={100}
        durationInFrames={Math.round(currentSlideDuration * videoConfig.fps) + 2}
      >
        <SlideWrapper
          style={{
            width: "100%",
            height: "100%",
            overflow: "hidden",
            position: "absolute",
          }}
        >
          {children}
        </SlideWrapper>
      </Sequence>
    );
  }

  // All transitions now play within the scene's own time window (starting at fromFrame),
  // so the Sequence always starts at the same position regardless of entry transition.
  const sequenceFrom = Math.max(0, fromFrame - 1);

  return (
    <Sequence
      key={slide}
      from={sequenceFrom}
      premountFor={100}
      durationInFrames={Math.round((currentSlideDuration + (nextTransitionDuration * 2)) * videoConfig.fps)}
    >
      {transitionComponents}
      <SlideWrapper
        style={{
          opacity:
            frame >= fromFrame - toLessFirst && frame <= toFrame + toLessLast ? 1 : 0,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          position: "absolute",
          ...style,
        }}
      >
        {children}
        <div style={innerStyle}></div>
      </SlideWrapper>
    </Sequence>
  );
};

export default SlideTransitions;